/**
 * The per-row 100%-stacked status bar.
 *
 * The table has 1,062 rows and no room for a chart in any of them, so the shape
 * of a row's traffic has to fit in ~8rem of width. A stacked bar does that: the
 * eye picks up a red sliver in a column of green bars far faster than it reads
 * a column of error-rate percentages.
 *
 * The important part of this component is what it refuses to draw. There are
 * three genuinely different states and only one of them is a bar:
 *
 *   total === null   Cloudflare reported nothing for this endpoint in this
 *                    window. We do not know what happened. Drawing a full-width
 *                    grey bar would assert a composition; drawing an empty one
 *                    would assert zero traffic. Both are claims we cannot make,
 *                    so we write "no data" and draw nothing.
 *   total === 0      We *did* look and the endpoint served nothing. That is a
 *                    real, different fact, and it gets its own words.
 *   total > 0        A bar.
 *
 * Colours come from `statusColors` so a red sliver here means exactly what a
 * red slice means in the overview bar and in the drill-down time series.
 */
import { useI18n } from '../i18n'
import { BUCKET_DESCRIPTION_KEYS, BUCKET_STYLES, bucketTotals, numberFormat, percent } from './statusColors'

export interface StatusRatioBarProps {
  /** `EndpointSummary.by_status` — JSON object keys are strings at runtime. */
  byStatus: Record<string, number>
  /** `null` means "no data", which is NOT the same as `0`. */
  total: number | null
}

export function StatusRatioBar({ byStatus, total }: StatusRatioBarProps) {
  const { t } = useI18n()

  if (total === null) {
    return (
      <span title={t('dashboard.ratioBar.noDataTitle')} className="text-xs text-muted">
        {t('dashboard.ratioBar.noData')}
      </span>
    )
  }

  if (total === 0) {
    return (
      <span title={t('dashboard.ratioBar.zeroTitle')} className="text-xs text-muted">
        {/* Not `dashboard.ratioBar.noData`, and never the same string: we
            looked, and the endpoint served nothing. See rule 5 in `en.ts`. */}
        {t('common.requestCount', { count: total })}
      </span>
    )
  }

  const buckets = bucketTotals(byStatus)
  if (buckets.length === 0) {
    // A positive total with no attributable statuses should be impossible;
    // saying so beats drawing a bar that adds up to nothing.
    return <span className="text-xs text-muted">{t('dashboard.ratioBar.noStatusDetail')}</span>
  }

  const label = buckets
    .map((b) =>
      t('dashboard.ratioBar.segment', {
        bucket: t(BUCKET_DESCRIPTION_KEYS[b.bucket]),
        share: percent(b.count / total),
      }),
    )
    .join(t('dashboard.ratioBar.separator'))

  return (
    <div
      data-testid="status-ratio-bar"
      role="img"
      aria-label={label}
      title={t('dashboard.ratioBar.title', {
        total: numberFormat.format(total),
        breakdown: label,
      })}
      className="flex h-2 w-full overflow-hidden rounded-full bg-border"
    >
      {buckets.map(({ bucket, count }) => (
        <span
          key={bucket}
          // Decorative: the whole composition is announced once on the parent.
          role="presentation"
          data-bucket={bucket}
          style={{ width: `${(count / total) * 100}%` }}
          className={BUCKET_STYLES[bucket].bg}
        />
      ))}
    </div>
  )
}
